import { jsonHeaders, requestCommon } from 'live/lib/utils'
import _ from 'lodash'
import { extractJson } from 'plugin-liverig/shared/asset/lib/utils'
import { convertModelsUnits } from '../modelsConfig/ModelsApi'
import { SourceUnitList } from '../modelsConfig/ModelsConfigTypes'
import { AlgorithmAttributes, AlgorithmFlagsResponse, AlgorithmResponse } from '../types/AlgorithmTypes'
import ConstantConfigType, { UnitConfig } from '../types/ConstantConfigType'
import ModelAlgorithmEnum from '../types/ModelAlgorithmEnum'
import { defineAlgorithmDetails, parameterUnits } from '../util/AlgorithmDetailsConverter'

const PLUGIN_PREFIX = '/services/plugin-rig-state'
const CONFIG = '/config'

const getRigStateAlgorithm = (modelAlgorithm: ModelAlgorithmEnum): Promise<AlgorithmAttributes> => {
    return fetch(PLUGIN_PREFIX + CONFIG + '/' + modelAlgorithm, {
        ...requestCommon,
        headers: jsonHeaders,
        method: 'GET'
    })
        .then(extractJson)
        .then((algorithmResponse: AlgorithmResponse) => defineAlgorithmDetails(algorithmResponse, modelAlgorithm))
}

const setRigStateAlgorithm = (
    modelAlgorithm: ModelAlgorithmEnum,
    algorithmAttributes: AlgorithmAttributes
): Promise<Response> => {
    return fetch(PLUGIN_PREFIX + CONFIG + '/' + modelAlgorithm, {
        ...requestCommon,
        headers: jsonHeaders,
        method: 'PUT',
        body: JSON.stringify(algorithmAttributes)
    })
}

const ConfigApi = {
    getRigStateAlgorithm: getRigStateAlgorithm,
    setRigStateAlgorithm: setRigStateAlgorithm,
    fetchOpModeAlgorithmList: (): Promise<Array<AlgorithmAttributes>> => {
        const algorithmRigState = getRigStateAlgorithm(ModelAlgorithmEnum.RIG_STATE_MODEL)
        const algorithLegacy = getRigStateAlgorithm(ModelAlgorithmEnum.LEGACY_MODEL)

        return Promise.all([algorithmRigState, algorithLegacy])
    },
    saveAll: async (algorithmArray: Array<AlgorithmAttributes>, unitName?: UnitConfig): Promise<Array<Response>> => {
        const newConstants = algorithmArray[0].constantsConvertedList
        const convertedConstants = await convertConstants(newConstants, 'default')

        const rigstateModel = { ...algorithmArray[0] }

        newConstants.forEach(({ key, value }) => {
            const sanitizeValue = !['enableSSAFiltering', 'hookLoadWithoutBlockWeight'].includes(key)
                ? parseFloat(
                      convertedConstants[key]?.targetValue?.toFixed(8) ||
                          convertedConstants[key]?.sourceValue?.toFixed(8)
                  )
                : value

            rigstateModel.constants[key] = sanitizeValue
        })

        const algorithmRigState = setRigStateAlgorithm(ModelAlgorithmEnum.RIG_STATE_MODEL, rigstateModel)
        const algorithLegacy = setRigStateAlgorithm(ModelAlgorithmEnum.LEGACY_MODEL, algorithmArray[1])

        return Promise.all([algorithmRigState, algorithLegacy])
    },
    setAnnotationConfig: (
        disableAutomaticDetection: boolean,
        disableMacroOperationInference: boolean,
        unitSetName: UnitConfig
    ): Promise<Response> => {
        return fetch(PLUGIN_PREFIX + CONFIG + '/general', {
            ...requestCommon,
            headers: jsonHeaders,
            method: 'PUT',
            body: JSON.stringify({ disableAutomaticDetection, disableMacroOperationInference, unitSetName })
        })
    },
    getAnnotationConfig: (): Promise<AlgorithmFlagsResponse> => {
        return fetch(PLUGIN_PREFIX + CONFIG + '/general', {
            ...requestCommon,
            headers: jsonHeaders,
            method: 'GET'
        }).then(extractJson)
    }
}

export const convertConstants = (constants: ConstantConfigType[], unitName: UnitConfig): Promise<SourceUnitList> => {
    const unitSet = parameterUnits[unitName]
    const parsedConstants: SourceUnitList = {}

    for (const { key, unit, value } of constants) {
        if (typeof value !== 'boolean') {
            parsedConstants[key] = {
                sourceValue: parseFloat(value?.toString()),
                sourceUnit: unit,
                targetUnit: unitSet[key],
                key
            }
        }
    }

    return convertModelsUnits(parsedConstants)
}

export const parseConvertedConstants = (converted: SourceUnitList, constants: ConstantConfigType[]) => {
    const newConstants: ConstantConfigType[] = [...constants]

    constants.forEach((constant, i) => {
        const targetUnit = converted[constant.key]?.targetUnit
        const targetValue = converted[constant.key]?.targetValue
        if (!_.isNumber(targetValue)) return
        newConstants[i].value = parseFloat(targetValue?.toFixed(8))
        newConstants[i].unit = targetUnit
    })
    return newConstants
}

export default ConfigApi
